/**
 * WoW-style HUD map:
 *
 *   - Round **minimap** anchored top-right, 180×180 px, centred on the
 *     player with rotation so the player always faces up.
 *   - **Full map** (toggled by M) overlaid on a dark backdrop, showing
 *     the whole world at a glance.
 *
 * Static layout (lake, forests, buildings, docks, menhirs) is drawn from
 * the {@link WorldStaticLayout} exported by nature.ts.  Dynamic state
 * (player, other players, enemies, loot) is supplied per frame via
 * {@link MinimapState}.
 *
 * Performance: 60 redraws/sec at ~200 draw calls each.  Canvas 2D is
 * plenty fast for this — main.ts calls update() once per render tick.
 */

import type { WorldStaticLayout } from '../scene/nature';

export interface MinimapEntity {
  id: string;
  x: number;
  z: number;
  /** Used for enemies (drawn larger when alive). */
  hp?: number;
  /** Optional label (other-player names, etc.). */
  label?: string;
}

export interface MinimapState {
  /** Local player position + yaw. */
  player: { x: number; z: number; yaw: number; name?: string };
  /** Other players in the world. */
  players: MinimapEntity[];
  /** Enemies (HP > 0 means alive). */
  enemies: MinimapEntity[];
  /** Loot on the ground. */
  loot: MinimapEntity[];
  /** When true (inside a house interior) the map shows a blank canvas. */
  inInterior?: boolean;
}

const COLOR_BG = '#161310';
const COLOR_WATER = '#1e3a5a';
const COLOR_SHALLOWS = '#2a4e6e';
const COLOR_BEACH = '#a89860';
const COLOR_GRASS = '#2a3e1e';
const COLOR_MEADOW = '#527038';
const COLOR_OAK = '#2e4220';
const COLOR_BIRCH = '#3a5226';
const COLOR_PINE = '#1e2c18';
const COLOR_BUILDING = '#7a7060';
const COLOR_KEEP = '#8a7a64';
const COLOR_TOWER = '#7a7060';
const COLOR_DOCK = '#5a3e22';
const COLOR_MENHIR = '#5a5a58';
const COLOR_PLAYER = '#ffe080';
const COLOR_PLAYER_ALLY = '#6090e0';
const COLOR_ENEMY = '#d04030';
const COLOR_LOOT = '#ffd040';
const COLOR_BORDER = '#5a4828';
const COLOR_TEXT = '#e0e0c8';

const MINIMAP_SIZE = 180; // px diameter
const MINIMAP_RANGE = 100; // metres shown around player (full diameter)
const FULLMAP_SIZE = 1100; // px square for the full map

export class Minimap {
  private readonly root: HTMLElement;
  private readonly minimapCanvas: HTMLCanvasElement;
  private readonly minimapCtx: CanvasRenderingContext2D;
  private readonly fullmapOverlay: HTMLDivElement;
  private readonly fullmapCanvas: HTMLCanvasElement;
  private readonly fullmapCtx: CanvasRenderingContext2D;
  private readonly fullmapCoords: HTMLDivElement;

  /** Big landmarks (lake, forests, buildings). */
  private readonly layout: WorldStaticLayout;

  /** Dynamic per-frame state. */
  private state: MinimapState = {
    player: { x: 0, z: 0, yaw: 0 },
    players: [],
    enemies: [],
    loot: [],
  };

  private mode: 'minimap' | 'fullmap' = 'minimap';

  constructor(layout: WorldStaticLayout) {
    this.layout = layout;

    // ---- DOM ----
    this.root = document.body;

    // Minimap corner widget
    this.minimapCanvas = document.createElement('canvas');
    this.minimapCanvas.classList.add('minimap-canvas');
    this.minimapCanvas.width = MINIMAP_SIZE;
    this.minimapCanvas.height = MINIMAP_SIZE;
    this.minimapCtx = this.minimapCanvas.getContext('2d')!;
    this.root.appendChild(this.minimapCanvas);

    // Tooltip showing world coords when hovering the minimap
    const tip = document.createElement('div');
    tip.classList.add('minimap-coords');
    this.minimapCanvas.addEventListener('mousemove', (e) => {
      const rect = this.minimapCanvas.getBoundingClientRect();
      const px = e.clientX - rect.left - MINIMAP_SIZE / 2;
      const py = e.clientY - rect.top - MINIMAP_SIZE / 2;
      // un-rotate by +playerYaw to convert to world delta
      const c = Math.cos(this.state.player.yaw);
      const s = Math.sin(this.state.player.yaw);
      const dx = px * c + py * s;
      const dy = -px * s + py * c;
      const scale = MINIMAP_RANGE / MINIMAP_SIZE;
      const wx = this.state.player.x + dx / scale;
      const wz = this.state.player.z - dy / scale;
      tip.textContent = `${wx.toFixed(0)}, ${wz.toFixed(0)}`;
      tip.style.left = `${e.clientX - rect.left + 12}px`;
      tip.style.top = `${e.clientY - rect.top + 12}px`;
    });
    this.minimapCanvas.addEventListener('mouseleave', () => {
      tip.textContent = '';
    });
    tip.textContent = '';
    this.minimapCanvas.parentElement?.appendChild(tip);
    this.minimapCanvas.addEventListener('click', () => this.toggle());

    // Full-map overlay
    this.fullmapOverlay = document.createElement('div');
    this.fullmapOverlay.classList.add('fullmap-overlay');
    this.fullmapCanvas = document.createElement('canvas');
    this.fullmapCanvas.classList.add('fullmap-canvas');
    this.fullmapCanvas.width = FULLMAP_SIZE;
    this.fullmapCanvas.height = FULLMAP_SIZE;
    this.fullmapCtx = this.fullmapCanvas.getContext('2d')!;
    this.fullmapCoords = document.createElement('div');
    this.fullmapCoords.classList.add('fullmap-coords');
    this.fullmapCoords.textContent = '[M] schließen';
    this.fullmapOverlay.appendChild(this.fullmapCanvas);
    this.fullmapOverlay.appendChild(this.fullmapCoords);
    this.fullmapCanvas.addEventListener('mousemove', (e) => {
      const rect = this.fullmapCanvas.getBoundingClientRect();
      const sx = (e.clientX - rect.left) / rect.width;
      const sy = (e.clientY - rect.top) / rect.height;
      const wx = (sx - 0.5) * 2 * layout.worldHalf;
      const wz = (sy - 0.5) * 2 * layout.worldHalf;
      this.fullmapCoords.textContent = `(${wx.toFixed(0)}, ${wz.toFixed(0)})  ·  [M] schließen`;
    });
    this.fullmapOverlay.addEventListener('click', () => this.toggle());
    this.root.appendChild(this.fullmapOverlay);

    // Pointer-lock click anywhere on the minimap should toggle, not steal
    // pointer-lock focus.  Prevent the canvas click from bubbling to
    // main.ts's lock handler.
    this.minimapCanvas.addEventListener('mousedown', (e) => e.stopPropagation());
    this.fullmapOverlay.addEventListener('mousedown', (e) => e.stopPropagation());
  }

  /** Toggle between minimap-only and full-map. */
  toggle(): void {
    if (this.mode === 'minimap') {
      this.mode = 'fullmap';
      this.fullmapOverlay.classList.add('visible');
    } else {
      this.mode = 'minimap';
      this.fullmapOverlay.classList.remove('visible');
    }
  }

  setState(state: MinimapState): void {
    this.state = state;
  }

  /** Called every render frame from main.ts. */
  render(): void {
    this.drawMinimap();
    if (this.mode === 'fullmap') this.drawFullmap();
  }

  // ---------------------------------------------------------------------
  // Minimap (corner widget, rotates with player)
  // ---------------------------------------------------------------------

  private drawMinimap(): void {
    const ctx = this.minimapCtx;
    const w = MINIMAP_SIZE;
    const cx = w / 2;
    const cy = w / 2;
    const scale = MINIMAP_RANGE / MINIMAP_SIZE; // metres per pixel

    ctx.clearRect(0, 0, w, w);

    // Clip to circle so off-map areas don't bleed
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, w / 2 - 2, 0, Math.PI * 2);
    ctx.clip();

    // Background
    ctx.fillStyle = COLOR_BG;
    ctx.fillRect(0, 0, w, w);

    // Translate to centre on player, then rotate so player heading is up
    ctx.translate(cx, cy);
    ctx.rotate(Math.PI - this.state.player.yaw);

    if (this.state.inInterior) {
      // Inside a house — show just a wooden floor disk so the player
      // knows the minimap is "off the world".
      ctx.fillStyle = '#5a3e22';
      ctx.beginPath();
      ctx.arc(0, 0, MINIMAP_RANGE * scale * 0.6, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Static world landmarks — pass player pos so they translate correctly
      this.drawWorld(ctx, this.state.player.x * scale, this.state.player.z * scale, scale);

      // Dynamic entities (in world coords, so add the inverse player offset)
      const px = this.state.player.x * scale;
      const pz = this.state.player.z * scale;
      this.drawPlayers(ctx, -px, -pz, scale, true);
      this.drawEnemies(ctx, -px, -pz, scale, true);
      this.drawLoot(ctx, -px, -pz, scale, true);
    }

    ctx.restore();

    // ---- North marker (small N at the top) ----
    ctx.fillStyle = COLOR_TEXT;
    ctx.font = 'bold 12px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // After rotate-back-to-zero, north is at canvas (cx, cy - r).
    ctx.fillText('N', cx, cy - w / 2 + 12);

    // ---- Border ring ----
    ctx.strokeStyle = COLOR_BORDER;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(cx, cy, w / 2 - 1.5, 0, Math.PI * 2);
    ctx.stroke();
    // Inner darker ring for depth
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, w / 2 - 4, 0, Math.PI * 2);
    ctx.stroke();

    // ---- Player triangle at centre ----
    // In the minimap we already rotated the canvas so the player's
    // forward direction points up, so the triangle is drawn without
    // an extra rotation here.
    this.drawPlayerTriangle(ctx, cx, cy, 0);
  }

  private drawPlayerTriangle(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    rotation: number
  ): void {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    ctx.fillStyle = COLOR_PLAYER;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, -8);
    ctx.lineTo(-6, 6);
    ctx.lineTo(0, 3);
    ctx.lineTo(6, 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // ---------------------------------------------------------------------
  // Full map (large overlay, no rotation)
  // ---------------------------------------------------------------------

  private drawFullmap(): void {
    const ctx = this.fullmapCtx;
    const w = FULLMAP_SIZE;
    const half = this.layout.worldHalf;
    const scale = w / (2 * half); // px per metre

    ctx.clearRect(0, 0, w, w);
    ctx.fillStyle = COLOR_BG;
    ctx.fillRect(0, 0, w, w);

    // Translate so world (0, 0) is at the canvas centre
    ctx.save();
    ctx.translate(w / 2, w / 2);
    // (no rotation — north stays at top)

    this.drawWorld(ctx, 0, 0, scale);

    // Dynamic entities in world coords
    this.drawPlayers(ctx, 0, 0, scale, false);
    this.drawEnemies(ctx, 0, 0, scale, false);
    this.drawLoot(ctx, 0, 0, scale, false);

    ctx.restore();

    // ---- Grid (10 m) ----
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let gx = -half; gx <= half; gx += 20) {
      const px = w / 2 + gx * scale;
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px, w);
      ctx.stroke();
    }
    for (let gz = -half; gz <= half; gz += 20) {
      const py = w / 2 + gz * scale;
      ctx.beginPath();
      ctx.moveTo(0, py);
      ctx.lineTo(w, py);
      ctx.stroke();
    }

    // ---- North arrow (top-centre, outside the world circle) ----
    ctx.fillStyle = COLOR_TEXT;
    ctx.font = 'bold 16px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.fillText('N', w / 2, 18);

    // ---- Compass labels at edges ----
    ctx.font = '11px "Courier New", monospace';
    ctx.fillStyle = 'rgba(224,224,200,0.5)';
    ctx.fillText('W', 16, w / 2);
    ctx.fillText('E', w - 16, w / 2);

    // ---- Player marker (always on top, no rotation) ----
    const ppx = w / 2 + this.state.player.x * scale;
    const ppy = w / 2 + this.state.player.z * scale;
    if (this.state.inInterior) {
      ctx.fillStyle = '#8a6840';
      ctx.beginPath();
      ctx.arc(ppx, ppy, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.stroke();
      ctx.fillStyle = COLOR_TEXT;
      ctx.font = 'bold 10px "Courier New", monospace';
      ctx.textAlign = 'center';
      ctx.fillText('INNEN', ppx, ppy + 3);
    } else {
      // Full map (no canvas rotation) — rotate the triangle by π - yaw
      // so it points in the player's actual facing direction.
      this.drawPlayerTriangle(ctx, ppx, ppy, Math.PI - this.state.player.yaw);
    }

    // ---- World boundary ----
    ctx.strokeStyle = 'rgba(224,224,200,0.4)';
    ctx.lineWidth = 2;
    ctx.strokeRect(2, 2, w - 4, w - 4);
  }

  // ---------------------------------------------------------------------
  // Shared world drawing
  // ---------------------------------------------------------------------

  /**
   * Draws the static world landmarks.  `offsetX/offsetZ` are subtracted
   * from each world XZ (so callers can recentre on the player).
   */
  private drawWorld(
    ctx: CanvasRenderingContext2D,
    offsetX: number,
    offsetZ: number,
    scale: number
  ): void {
    // ---- Lake (under everything else) ----
    ctx.fillStyle = COLOR_WATER;
    const main = this.layout.lake.main;
    this.fillRectWorld(
      ctx,
      main.x - main.w / 2 - offsetX,
      main.z - main.h / 2 - offsetZ,
      main.w,
      main.h,
      scale
    );
    for (const bay of this.layout.lake.bays) {
      this.fillRectWorld(
        ctx,
        bay.x - bay.w / 2 - offsetX,
        bay.z - bay.h / 2 - offsetZ,
        bay.w,
        bay.h,
        scale
      );
    }
    // Shallows + beach on top
    const sh = this.layout.lake.shallows;
    ctx.fillStyle = COLOR_SHALLOWS;
    this.fillRectWorld(
      ctx,
      sh.x - sh.w / 2 - offsetX,
      sh.z - sh.h / 2 - offsetZ,
      sh.w,
      sh.h,
      scale
    );
    const beach = this.layout.lake.beach;
    ctx.fillStyle = COLOR_BEACH;
    this.fillRectWorld(
      ctx,
      beach.x - beach.w / 2 - offsetX,
      beach.z - beach.h / 2 - offsetZ,
      beach.w,
      beach.h,
      scale
    );

    // Island
    const island = this.layout.lake.island;
    ctx.fillStyle = COLOR_BEACH;
    this.fillRectWorld(
      ctx,
      island.x - island.w / 2 - offsetX,
      island.z - island.h / 2 - offsetZ,
      island.w,
      island.h,
      scale
    );

    // ---- Grass tint (full world, under everything) ----
    ctx.fillStyle = COLOR_GRASS;
    ctx.fillRect(
      (-this.layout.worldHalf - offsetX) * scale,
      (-this.layout.worldHalf - offsetZ) * scale,
      this.layout.worldHalf * 2 * scale,
      this.layout.worldHalf * 2 * scale
    );
    // Re-paint lake on top of grass
    ctx.fillStyle = COLOR_WATER;
    this.fillRectWorld(
      ctx,
      main.x - main.w / 2 - offsetX,
      main.z - main.h / 2 - offsetZ,
      main.w,
      main.h,
      scale
    );
    for (const bay of this.layout.lake.bays) {
      this.fillRectWorld(
        ctx,
        bay.x - bay.w / 2 - offsetX,
        bay.z - bay.h / 2 - offsetZ,
        bay.w,
        bay.h,
        scale
      );
    }

    // ---- Meadows (lighter green patches) ----
    for (const m of this.layout.meadows) {
      ctx.fillStyle = COLOR_MEADOW;
      this.fillRectWorld(
        ctx,
        m.x - m.w / 2 - offsetX,
        m.z - m.h / 2 - offsetZ,
        m.w,
        m.h,
        scale
      );
    }

    // ---- Forest regions (dark green, with a stippled overlay) ----
    for (const f of this.layout.forests) {
      const c =
        f.kind === 'oak'
          ? COLOR_OAK
          : f.kind === 'birch'
          ? COLOR_BIRCH
          : COLOR_PINE;
      ctx.fillStyle = c;
      this.fillRectWorld(
        ctx,
        f.bounds.x - f.bounds.w / 2 - offsetX,
        f.bounds.z - f.bounds.h / 2 - offsetZ,
        f.bounds.w,
        f.bounds.h,
        scale
      );
      // Stipple — small darker dots to suggest trees
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      const wpx = f.bounds.w * scale;
      const hpx = f.bounds.h * scale;
      const xpx = (f.bounds.x - f.bounds.w / 2 - offsetX) * scale;
      const ypx = (f.bounds.z - f.bounds.h / 2 - offsetZ) * scale;
      for (let dy = 4; dy < hpx; dy += 6) {
        for (let dx = 4; dx < wpx; dx += 6) {
          ctx.fillRect(xpx + dx, ypx + dy, 1, 1);
        }
      }
    }

    // ---- Buildings ----
    for (const b of this.layout.buildings) {
      const c =
        b.kind === 'keep'
          ? COLOR_KEEP
          : b.kind === 'tower'
          ? COLOR_TOWER
          : b.kind === 'wall'
          ? '#4a4036'
          : COLOR_BUILDING;
      ctx.fillStyle = c;
      const xpx = (b.bounds.x - b.bounds.w / 2 - offsetX) * scale;
      const ypx = (b.bounds.z - b.bounds.h / 2 - offsetZ) * scale;
      ctx.fillRect(xpx, ypx, b.bounds.w * scale, b.bounds.h * scale);
      // outline
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = 1;
      ctx.strokeRect(xpx, ypx, b.bounds.w * scale, b.bounds.h * scale);
    }

    // ---- Docks ----
    ctx.fillStyle = COLOR_DOCK;
    for (const d of this.layout.docks) {
      const xpx = (d.x - d.w / 2 - offsetX) * scale;
      const ypx = (d.z - d.h / 2 - offsetZ) * scale;
      ctx.fillRect(xpx, ypx, d.w * scale, d.h * scale);
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.strokeRect(xpx, ypx, d.w * scale, d.h * scale);
    }

    // ---- Menhirs ----
    ctx.fillStyle = COLOR_MENHIR;
    for (const m of this.layout.menhirs) {
      const xpx = (m.x - offsetX) * scale - 2;
      const ypx = (m.z - offsetZ) * scale - 2;
      ctx.fillRect(xpx, ypx, 4, 4);
    }
  }

  private fillRectWorld(
    ctx: CanvasRenderingContext2D,
    x: number,
    z: number,
    w: number,
    h: number,
    scale: number
  ): void {
    ctx.fillRect(x * scale, z * scale, w * scale, h * scale);
  }

  // ---------------------------------------------------------------------
  // Dynamic entities
  // ---------------------------------------------------------------------

  private drawPlayers(
    ctx: CanvasRenderingContext2D,
    offsetX: number,
    offsetZ: number,
    scale: number,
    minimap: boolean
  ): void {
    for (const p of this.state.players) {
      const x = (p.x - offsetX) * scale;
      const z = (p.z - offsetZ) * scale;
      ctx.fillStyle = COLOR_PLAYER_ALLY;
      ctx.beginPath();
      ctx.arc(x, z, minimap ? 3 : 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();
      if (!minimap && p.label) {
        ctx.fillStyle = COLOR_TEXT;
        ctx.font = '11px "Courier New", monospace';
        ctx.textAlign = 'center';
        ctx.fillText(p.label, x, z - 8);
      }
    }
  }

  private drawEnemies(
    ctx: CanvasRenderingContext2D,
    offsetX: number,
    offsetZ: number,
    scale: number,
    minimap: boolean
  ): void {
    for (const e of this.state.enemies) {
      if (e.hp !== undefined && e.hp <= 0) continue; // dead enemies hidden
      const x = (e.x - offsetX) * scale;
      const z = (e.z - offsetZ) * scale;
      const r = minimap ? 3.5 : 5;
      // Red square = aggressive (WoW style)
      ctx.fillStyle = COLOR_ENEMY;
      ctx.fillRect(x - r, z - r, r * 2, r * 2);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(x - r, z - r, r * 2, r * 2);
    }
  }

  private drawLoot(
    ctx: CanvasRenderingContext2D,
    offsetX: number,
    offsetZ: number,
    scale: number,
    minimap: boolean
  ): void {
    for (const l of this.state.loot) {
      const x = (l.x - offsetX) * scale;
      const z = (l.z - offsetZ) * scale;
      ctx.fillStyle = COLOR_LOOT;
      ctx.beginPath();
      ctx.arc(x, z, minimap ? 2 : 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
}
